import { useMemo } from "react";
import { useAppSelector } from "../../../redux";
import {
  useSessionCanAccessModule,
  useSessionCanEditModule,
  useSessionPermissionsLoaded,
  useSessionPermissionsLoading,
} from "../../../hooks/useSessionPermissions";
import {
  usePartnerSessionCanAccessModule,
  usePartnerSessionCanAccessProjects,
  usePartnerSessionPermissionsLoaded,
  usePartnerSessionPermissionsLoading,
} from "../../../hooks/usePartnerSessionPermissions";
import { PARTNER_MANAGEMENT_PERMISSION_KEYS as PM_KEYS } from "../../../utils/permissions/partnerManagementPermissionKeys";
import { PARTNER_PORTAL_PERMISSION_KEYS as PP_KEYS } from "../../../utils/permissions/partnerPortalPermissionKeys";
import type { DashboardVariant } from "../config/dashboardBlocks";

export type DashboardPermissions = {
  canEditSupportStatus: boolean;
  viewAllOrdersEnabled: boolean;
  viewAllProjectsEnabled: boolean;
};

function resolveViewAllEnabled(
  roleId: number | undefined,
  loading: boolean,
  loaded: boolean,
  canAccess: boolean
): boolean {
  if (!roleId) return true;
  if (loading || !loaded) return false;
  return canAccess;
}

export function useDashboardPermissions(
  variant: DashboardVariant
): DashboardPermissions {
  const roleId = useAppSelector((state) => state.auth.loggedUserDetail?.role_id);

  const adminLoading = useSessionPermissionsLoading();
  const adminLoaded = useSessionPermissionsLoaded();
  const partnerLoading = usePartnerSessionPermissionsLoading();
  const partnerLoaded = usePartnerSessionPermissionsLoaded();

  const canEditAdminDashboard = useSessionCanEditModule(
    PM_KEYS.PARTNER_ADMIN_DASHBOARD
  );
  const canAccessAdminOrders = useSessionCanAccessModule(
    PM_KEYS.ADMIN_ORDER_MANAGEMENT
  );
  const canAccessPartnerView = useSessionCanAccessModule(
    PM_KEYS.ACCESS_PARTNER_VIEW
  );

  const canAccessPartnerOrders = usePartnerSessionCanAccessModule(
    PP_KEYS.PARTNER_ORDER_MANAGEMENT
  );
  /** Prefer access_projects; fall back to partner_project_list when needed. */
  const canAccessProjects = usePartnerSessionCanAccessProjects();

  return useMemo(() => {
    if (variant === "admin") {
      return {
        canEditSupportStatus: canEditAdminDashboard,
        viewAllOrdersEnabled: resolveViewAllEnabled(
          roleId,
          adminLoading,
          adminLoaded,
          canAccessAdminOrders
        ),
        viewAllProjectsEnabled: resolveViewAllEnabled(
          roleId,
          adminLoading,
          adminLoaded,
          canAccessPartnerView && canAccessProjects
        ),
      };
    }

    return {
      canEditSupportStatus: false,
      viewAllOrdersEnabled: resolveViewAllEnabled(
        roleId,
        partnerLoading,
        partnerLoaded,
        canAccessPartnerOrders
      ),
      viewAllProjectsEnabled: resolveViewAllEnabled(
        roleId,
        partnerLoading,
        partnerLoaded,
        canAccessProjects
      ),
    };
  }, [
    variant,
    roleId,
    adminLoading,
    adminLoaded,
    partnerLoading,
    partnerLoaded,
    canEditAdminDashboard,
    canAccessAdminOrders,
    canAccessPartnerView,
    canAccessPartnerOrders,
    canAccessProjects,
  ]);
}
